import { WellLogsWidgetState } from '@go-widgets/well-logs-widget';
import type { InitParams, WellIndexType, WidgetStateParams } from '@go-widgets/well-logs-widget';
import { STYLE_VARIANTS, type GlobalConfig } from '@repo/go-ui';
import type { VideoBroadcastStateParams } from '@repo/go-widgets';
import { action, comparer, flow, makeObservable, observable, reaction } from 'mobx';

import { EXPORT_CSV_AVAILABLE, OPERATIONS_LOG_TRACK_AVAILABLE, VIDEOBROADCAST_AVAILABLE } from 'src/api/consts';
import { lagacyAgent } from 'src/api/legacyAgent';
import { wellLogsWidgetAgent } from 'src/api/wellLogsWidgetAgent';
import type { TCreateWidgetOptions, TGroup, TWellListCombinedData } from 'src/entities/tab/TabEntity';
import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import { requireService, requireServiceAccessor } from 'src/packages/di';
import { IS_PORTABLE_DEVICE } from 'src/packages/shared/consts/isPortableDevice';

import { setWellIdInGroup } from '../../utils/setWellIdInGroup';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';
import { getLinkedVideoTimeCoordinator } from '../wellLogs-video-link/LinkedVideoTimeCoordinator';

import type { WellLogsWidgetEntity } from './WellLogsWidget.entity';
import { getCorrectLocale } from './WellLogsWidget.utils';

export class WellLogsWidgetStore implements IWidgetStore {
  private readonly wellLogsEntity: WellLogsWidgetEntity;
  private readonly groupSelectStore: GroupSelectStore;
  private readonly initParams: InitParams;

  @observable isLoading: boolean = false;
  @observable isExitFullscreenFetching: boolean = false;
  @observable isReady: boolean = false;
  @observable statusBarValue?: number | null;
  @observable hasError: boolean = false;
  @observable progress: number | null = null;
  @observable isGroupSelectOpen: boolean = false;

  @observable curveRenderingMode: 'mesh' | 'legacy' = 'mesh';

  onCreateWellLogsWidget: (
    wellIndexType: WellIndexType,
    wellId: number | null,
    stateParams: Partial<WidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ) => void;
  onCreateVideobroadcastWidget: (
    wellId: number | null,
    stateParams: Partial<VideoBroadcastStateParams> | null,
    options?: TCreateWidgetOptions,
  ) => void;

  constructor(
    wellLogsEntity: WellLogsWidgetEntity,
    groupSelectStore: GroupSelectStore,
    onCreateWellLogsWidget: (
      wellIndexType: WellIndexType,
      wellId: number | null,
      stateParams: Partial<WidgetStateParams> | null,
      options?: TCreateWidgetOptions,
    ) => void,
    onCreateVideobroadcastWidget: (
      wellId: number | null,
      stateParams: Partial<VideoBroadcastStateParams> | null,
      options?: TCreateWidgetOptions,
    ) => void,
    private readonly wellListPanel: TWellListCombinedData,
    private readonly theme = requireService('theme'),
    private readonly otherSettings = requireService('otherSettings'),
    private readonly stompCachePublisherService = requireService('stompCachePublisherService'),
    private readonly stompOperationsPublisherService = requireService('stompOperationsPublisherService'),
    private readonly wellStatusService = requireService('wellStatusService'),
    private readonly notifications = requireService('notifications'),
    private readonly language = requireService('language'),
    private readonly wellLogsTemplateService = requireService('wellLogsTemplateService'),
    private readonly getAuthService = requireServiceAccessor('authService'),
    readonly classOfUnitsService = requireService('classOfUnitsService'),
    private readonly dashboardService = requireService('dashboardService'),
    private readonly wellboresRegistry = requireService('wellboresRegistry'),
    private readonly operationsService = requireService('operationsService'),
    private readonly generatedUserFilesService = requireService('generatedUserFilesService'),
    private readonly userPreferencesService = requireService('userPreference'),
  ) {
    this.onCreateWellLogsWidget = onCreateWellLogsWidget;
    this.onCreateVideobroadcastWidget = onCreateVideobroadcastWidget;

    this.wellLogsEntity = wellLogsEntity;
    const { wellIndexType } = wellLogsEntity;
    this.groupSelectStore = groupSelectStore;
    const wellLogsTemplatesManager = this.wellLogsTemplateService.getTemplatesManager(wellIndexType);
    this.isGroupSelectOpen = wellLogsEntity.isGroupsDefaultOpened;

    this.initParams = {
      widgetType: 'desktop',
      legacyAxiosInstance: lagacyAgent,
      index: wellIndexType,
      wellLogsStompPublisher: this.stompCachePublisherService.stompPublisher,
      operationsStompPublisher: this.stompOperationsPublisherService.stompPublisher,
      wellDirectoryServiceStompPublisher: this.wellStatusService,
      operationsService: this.operationsService,
      axiosInstance: wellLogsWidgetAgent,
      userData: {
        sub: this.getAuthService().userInfo.sub,
      },
      templatesManager: wellLogsTemplatesManager.templatesManager,
      userSettingsManager: wellLogsTemplatesManager.userSettings,
      splitRange: {
        time: this.userPreferencesService.preferences.defaultSplitRange.time,
        depth: this.userPreferencesService.preferences.defaultSplitRange.depth,
      },
      classOfUnitsService,
      dashboardService: this.dashboardService,
      wellboresRegistry: this.wellboresRegistry,
      operationsLogEnabled: OPERATIONS_LOG_TRACK_AVAILABLE,
      defaultDepthUnits:
        this.userPreferencesService.preferences.defaultDepthUnits === 'INDEX_DEPTH_SCALE_METERS' ? 'm' : 'ft',
      exportCsvEnabled: EXPORT_CSV_AVAILABLE,
    };

    makeObservable(this);
  }

  @action.bound
  setIsGroupSelectOpen(isOpen: boolean): void {
    this.isGroupSelectOpen = isOpen;
  }

  @action.bound
  enableEditMode(): void {
    if (IS_PORTABLE_DEVICE) return;

    const state = this.wellLogsEntity.state?.enterEditMode();
    if (state) {
      this.wellLogsEntity.setFullscreen(true);
    }
  }

  @flow.bound
  async *exitFullScreen(themeConfig: GlobalConfig) {
    if (this.isExitFullscreenFetching) return;

    this.isExitFullscreenFetching = true;

    const enableToClose = await this.wellLogsEntity.state?.exitEditModeAsync({
      ...themeConfig,
      theme: [STYLE_VARIANTS.dark, STYLE_VARIANTS.light].includes(themeConfig.theme) ? themeConfig.theme : undefined,
    });

    yield;

    if (enableToClose) {
      this.wellLogsEntity.setFullscreen(false);
    }

    this.isExitFullscreenFetching = false;
  }

  handleExport = (): void => {
    this.wellLogsEntity.state?.exportData();
  };

  handleReloadWellStructure = (): void => {
    this.wellLogsEntity.state?.reloadWellStructure();
  };

  @action.bound
  setGroupForWellLogs(group: TGroup | null): void {
    this.wellLogsEntity.setGroup(group ? group.id : null);
  }

  @action.bound
  setWellId(well: number | null): void {
    this.wellLogsEntity.setWellId(well);
  }

  @action.bound
  toggleCurveRenderMode() {
    this.curveRenderingMode = this.curveRenderingMode === 'mesh' ? 'legacy' : 'mesh';
  }

  @action.bound
  private setReady(value: boolean): void {
    this.isReady = value;
  }

  @action.bound
  private setProgress(value: number | null) {
    this.progress = value;
  }

  @action.bound
  private setInActiveTab(value: boolean) {
    this.wellLogsEntity.setInActiveTab(value);
  }

  private getNewState(wellId: number): WellLogsWidgetState {
    return new WellLogsWidgetState(
      this.initParams,
      {
        // TODO: remove hardcode
        wellId,
        width: 500,
        height: 500,
        locale: getCorrectLocale(this.language.language),
        theme: this.theme.theme,
        fullScreen: this.wellLogsEntity.isFullscreen,
        active: this.wellLogsEntity.inActiveTab,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
        isColoredHeaders: this.userPreferencesService.preferences.isColoredParamsHeaders,
        fpsLock: this.otherSettings.graphicsPerformanceFps,
        curveRenderingMode: this.curveRenderingMode,
        defaultSelectWellbore:
          this.wellLogsEntity.wellIndexType === 'LOG_TIME'
            ? this.userPreferencesService.preferences.autoSelectWellboreForTime
            : this.userPreferencesService.preferences.autoSelectWellboreForDepth,
        isVideoWidgetAvailable: VIDEOBROADCAST_AVAILABLE,
      },
      this.wellLogsEntity.stateParams,
      {
        onEditModeChanged: (state) => {
          this.wellLogsEntity.setEditMode(state);
        },
        onInitializedSuccess: () => {
          this.setReady(true);
        },
        onInitializationFailed: (_, error) => {
          this.setReady(false);

          console.error(error);
          this.notifications.showErrorMessageT('errors:failedToInitWidget');
        },
        onInternalStateChanged: (state) => {
          this.wellLogsEntity.setStateParams(state);
        },
        onCloneWidgetRequested: (state) => {
          const { wellIndexType, wellId } = this.wellLogsEntity;
          this.onCreateWellLogsWidget(wellIndexType, wellId, state);
        },
        onLinkedVideoWidgetOpenRequested: () => {
          if (!VIDEOBROADCAST_AVAILABLE) return;

          getLinkedVideoTimeCoordinator(this.groupSelectStore).ensureVideoForWellLogs(
            this.wellLogsEntity,
            this.onCreateVideobroadcastWidget,
          );
        },
        onTimeSelected: (timeMs) => {
          if (!VIDEOBROADCAST_AVAILABLE) return;

          getLinkedVideoTimeCoordinator(this.groupSelectStore).publishFromWellLogs(this.wellLogsEntity, timeMs);
        },
        onVideoIntervalSelected: (interval) => {
          if (!VIDEOBROADCAST_AVAILABLE) return;

          getLinkedVideoTimeCoordinator(this.groupSelectStore).publishIntervalFromWellLogs(
            this.wellLogsEntity,
            interval,
          );
        },
        onLoaderStatusChanged: (state) => {
          this.setProgress(state ? Math.round(state) : state);
        },
        onWellboreChanged: (wellboreName) => {
          this.wellLogsEntity.setWellboreName(wellboreName);
        },
        onExportCsvStarted: (taskId) => {
          this.generatedUserFilesService.addTask(taskId);
        },
      },
    );
  }

  effect = () => {
    this.setInActiveTab(true);
    const linkedVideoCoordinator = VIDEOBROADCAST_AVAILABLE
      ? getLinkedVideoTimeCoordinator(this.groupSelectStore)
      : null;

    const handleVisibilityChange = (): void => {
      this.setInActiveTab(document.visibilityState === 'visible');
    };
    window.addEventListener('visibilitychange', handleVisibilityChange);

    const disposeGroupId = reaction(
      () => this.wellLogsEntity.groupId,
      (groupId) => {
        if (groupId === null) return;

        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
        setWellIdInGroup(widgets, this.wellLogsEntity, this.wellListPanel);
      },
    );

    const disposeWellId = reaction(
      () => this.wellLogsEntity.wellId,
      (wellId, prevWellId) => {
        if (wellId && !this.wellLogsEntity.state) {
          const newState = this.getNewState(wellId);

          this.wellLogsEntity.setState(newState);
        } else if (wellId && this.wellLogsEntity.state && typeof prevWellId !== 'undefined') {
          this.wellLogsEntity.state.setAppParams({ wellId });
        } else if (!wellId && this.wellLogsEntity.state) {
          this.wellLogsEntity.state.destroy();
          this.wellLogsEntity.setState(null);
        }
        this.setProgress(null);
      },
      { fireImmediately: true },
    );

    const disposeStateParams = reaction(
      () => ({
        theme: this.theme.theme,
        language: this.language.language,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
        curveRenderingMode: this.curveRenderingMode,
      }),
      ({ theme, language, isAltNames, curveRenderingMode }) => {
        this.wellLogsEntity.state?.setAppParams({
          theme,
          locale: getCorrectLocale(language),
          isAltNames,
          curveRenderingMode,
        });
      },
      {
        fireImmediately: true,
      },
    );

    const wellListPanelDisposer = reaction(
      () => this.wellListPanel.store.selectedWellId,
      (wellId) => {
        if (this.wellListPanel.panelState?.groupId === this.wellLogsEntity.groupId) {
          this.wellLogsEntity.setWellId(wellId);
        }
      },
      {
        fireImmediately: true,
      },
    );

    const linkedVideoSyncDisposer = reaction(
      () => {
        const linkedVideoSync = this.wellLogsEntity.state?.linkedVideoSync ?? null;

        return {
          groupId: this.wellLogsEntity.groupId,
          wellId: this.wellLogsEntity.wellId,
          linkedVideoSyncEnabled: linkedVideoSync
            ? linkedVideoSync.enabled
            : Boolean(this.wellLogsEntity.stateParams.linkedVideoSyncEnabled),
          linkedVideoSyncTimeMs: linkedVideoSync
            ? linkedVideoSync.markerTimeMs
            : (this.wellLogsEntity.stateParams.linkedVideoSyncTimeMs ?? null),
        };
      },
      ({ groupId, wellId }, previousState) => {
        if (!linkedVideoCoordinator) return;

        if (previousState && (previousState.groupId !== groupId || previousState.wellId !== wellId)) {
          linkedVideoCoordinator.refreshVideoSyncForGroup(previousState.groupId, previousState.wellId);
        }

        linkedVideoCoordinator.refreshVideoSyncForWidget(this.wellLogsEntity);
      },
      {
        fireImmediately: true,
        equals: comparer.structural,
      },
    );

    return () => {
      this.setInActiveTab(false);
      disposeGroupId();
      disposeWellId();
      disposeStateParams();
      wellListPanelDisposer();
      linkedVideoSyncDisposer();
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  };
}
